import { safeLocal } from '@/utils/safeStorage';

const OPPREF_COOKIE = '__oppref';
const OPPREF_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

/** Same key the consent banner writes. The pixel must not load on any other signal. */
export const ANALYTICS_CONSENT_STORAGE_KEY = 'tsl_analytics_consent_v1';
export const OPENAI_ADS_PIXEL_ID = 'EpKmTZYN423cVNi6Ti5RY5';
export const OPENAI_ADS_SDK_URL = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';

type OaiqFn = ((...args: unknown[]) => void) & { q?: unknown[][] };

export function hasAnalyticsConsent(): boolean {
  return safeLocal.get(ANALYTICS_CONSENT_STORAGE_KEY) === 'granted';
}

/** Debug beacons stay off. A requested true does not override production. */
export function openAIAdsDebug(_environment?: string, _requested?: boolean): boolean {
  return false;
}

export function loadOpenAIAdsPixel(): void {
  if (typeof document === 'undefined') return;
  if (!hasAnalyticsConsent()) return;
  // Already installed by this loader. A second call must not reset the queue.
  if (document.querySelector('script[data-openai-ads="1"]')) return;

  const w = window as Window & { oaiq?: OaiqFn };
  // The GTM container also creates this queue and inits it with debug on.
  // Drop that before installing the consent-gated init.
  delete w.oaiq;

  const queued: unknown[][] = [];
  const queue = ((...args: unknown[]) => {
    queued.push(args);
  }) as OaiqFn;
  queue.q = queued;
  w.oaiq = queue;

  w.oaiq('init', { pixelId: OPENAI_ADS_PIXEL_ID, debug: openAIAdsDebug() });
  const script = document.createElement('script');
  script.async = true;
  // The HTML gate allows this host only when the marker is already set.
  script.dataset.openaiAds = '1';
  script.src = OPENAI_ADS_SDK_URL;
  const first = document.getElementsByTagName('script')[0];
  if (first?.parentNode) first.parentNode.insertBefore(script, first);
  else document.head.appendChild(script);
}

export function unloadOpenAIAdsPixel(): void {
  if (typeof document !== 'undefined') {
    document.querySelectorAll(
      'script[data-openai-ads="1"], script[src*="bzrcdn.openai.com"]',
    ).forEach((node) => node.remove());
  }
  if (typeof window !== 'undefined') {
    delete (window as Window & { oaiq?: OaiqFn }).oaiq;
  }
}

export function buildOpprefCookie(value: string, hostname: string): string {
  const attributes = [
    `${OPPREF_COOKIE}=${encodeURIComponent(value)}`,
    `Max-Age=${OPPREF_MAX_AGE_SECONDS}`,
    'Path=/',
    'SameSite=Lax',
  ];

  if (hostname === 'tacostreetlocating.com' || hostname.endsWith('.tacostreetlocating.com')) {
    attributes.push('Domain=.tacostreetlocating.com', 'Secure');
  }

  return attributes.join('; ');
}

/**
 * The OpenAI click reference arrives on ai.tacostreetlocating.com, while the
 * completion pixel fires after involve.me redirects to tacostreetlocating.com.
 * Store the reference on the parent domain so both Taco Street hosts share the
 * same 30-day first-party attribution cookie across that redirect.
 */
export function captureOpenAIAdsAttribution(): void {
  if (typeof window === 'undefined') return;

  const oppref = new URLSearchParams(window.location.search).get('oppref')?.trim();
  if (!oppref) return;

  document.cookie = buildOpprefCookie(oppref, window.location.hostname);
}

/**
 * Record a confirmed lead with the OpenAI Ads pixel. The bootstrap snippet
 * installs a queueing function before the SDK finishes loading, so this is
 * safe to call as soon as the backend returns a successful lead ID.
 */
export function measureOpenAILead(eventId: string): boolean {
  if (typeof window === 'undefined') return false;
  if (!hasAnalyticsConsent()) return false;

  const oaiq = (window as Window & {
    oaiq?: (...args: unknown[]) => void;
  }).oaiq;
  if (typeof oaiq !== 'function') return false;

  oaiq(
    'measure',
    'lead_created',
    { type: 'customer_action' },
    { event_id: eventId },
  );
  return true;
}
